import { Card, Row } from 'antd';
import { useEffect, useState } from 'react';
import { CalendarCommon } from '../../common/calendar-common/CalendarCommon';
import DrawerCommon from '../../common/drawer-common/DrawerCommon';

import './RequestsCalendarOutline.scss';

import { ICalendarCommon, ISelectedDays } from '../../../utils/calendarUtility';

import { useTranslate } from '@tolgee/react';
import { PERMISSION_CODES } from '../../../constants/permissionCodes';
import { useGlobalContext } from '../../../global-context/GlobalContext';
import useIsMobile from '../../../hooks/utils/useIsMobile';
import { useTastingService } from '../../../hooks/utils/useTastingService';
import { useAccessControl } from '../../../hooks/usePermission';
import { openConfirmationPopup, showToast } from '../../../utils/commonUtility';
import { displayCenter } from '../../../utils/styleobjects';
import { CalendarMobile } from '../../common/calendar-mobile/CalendarMobile';
import {
  CancelledIcon,
  ConfirmedIcon,
  PendingIcon,
} from '../../common/create-icon/CreateIcon';
import {
  ConfirmationModal,
  ReservationCancelled,
  ReservedSuccess,
} from '../confirmation-modal/ConfirmationModal';
import { OrderRequestForm } from '../order-request-form/OrderRequestForm';
import { RequestsRemarks } from '../request-remarks/RequestsRemarks';
export enum TastingStatuses {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELED = 'canceled',
}

export const tastingStutusesData: any = {
  [TastingStatuses.PENDING]: {
    color: '#fbb071',
    Icon: PendingIcon,
    text: 'pending',
  },
  [TastingStatuses.CONFIRMED]: {
    color: '#cbce94',
    Icon: ConfirmedIcon,
    text: 'confirmed',
  },
  [TastingStatuses.CANCELED]: {
    color: '#ff735b',
    Icon: CancelledIcon,
    text: 'canceled',
  },
};

export const CalendarOutline: React.FC = () => {
  const { can } = useAccessControl();
  const isMobile = useIsMobile();
  const { t } = useTranslate();
  const isRemarksOpen =
    useGlobalContext(c => c.app.openeRemarksdPanelType) == 'EVENTS';
  const {
    getBookingsBtwDates,
    tastingData,
    refetchGetBookingsBtwDates,
    bookTasting,
    bookTastingLoading,
    cancelTasting,
    disabledDates,
  } = useTastingService();
  const [addEventFormOpen, showAddEventForm] = useState(false);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [fromToDates, setFromToDates] = useState<[Date, Date] | undefined>(
    undefined,
  );
  const [confirmationProps, setConfirmationProps] = useState<{
    isOpen: boolean;
    date?: Date;
  }>({
    isOpen: false,
    date: undefined,
  });
  const [selectedStaffingDay, setStaffingDay] = useState<
    ISelectedDays | undefined
  >(undefined);
  const setShowAddEventForm = (bool: boolean) => {
    setStaffingDay(undefined);
    showAddEventForm(bool);
  };

  const onDeleteClick = (selectedEvent: ISelectedDays) => {
    if (!can(PERMISSION_CODES.TASTING_BOOKINGS_CANCEL)) {
      showToast(
        'You do not have permission to cancel tasting requests',
        'error',
      );
      return;
    }
    openConfirmationPopup({
      title: t('do-you-really-want-to-delete-this-event?'),
      onOkCB: () => {
        cancelTasting(selectedEvent.id, data => {
          if (data?.cancelTastingReservation?.data?.isCancelled) {
            refetchGetBookingsBtwDates();
            setConfirmationProps({ isOpen: true });
          }
        });
      },
      okText: t('yes'),
      cancelText: t('no'),
    });
  };

  const onSubmitForm = (values: any) => {
    if (!can(PERMISSION_CODES.TASTING_BOOKINGS_BOOK)) {
      showToast('You do not have permission to book tasting requests', 'error');
      return;
    }
    const { bookingDate, numberOfPeers, comments } = values;
    bookTasting(bookingDate, comments, numberOfPeers, data => {
      if (data?.bookTasting?.data?.isCreated) {
        setShowAddEventForm(false);
        refetchGetBookingsBtwDates();
        setConfirmationProps({ isOpen: true, date: bookingDate });
      }
    });
  };

  useEffect(() => {
    if (isMobile) {
      const now = new Date();
      getBookingsBtwDates(
        new Date(now.getFullYear(), now.getMonth(), 1),
        new Date(now.getFullYear(), now.getMonth() + 1, 0),
      );
    } else {
      getBookingsBtwDates(
        new Date(year, 0, 1),
        new Date(year, 11, 31, 23, 59, 59, 999),
      );
    }
  }, [year]);

  return (
    <Row
      justify='center'
      style={{ flexWrap: 'unset', height: '100%', textAlign: 'start' }}
    >
      <CalendarWrapper
        onDateClickHandler={date => {
          if (!can(PERMISSION_CODES.TASTING_BOOKINGS_BOOK)) {
            showToast(
              'You do not have permission to book tasting requests',
              'error',
            );
            return;
          }
          showAddEventForm(true);
          setFromToDates([date, date]);
        }}
        onDelete={onDeleteClick}
        selectedDays={tastingData ?? []}
        onYearChange={(year: number) => {
          setYear(year);
        }}
        onMonthChange={(year: number, month: number) => {
          getBookingsBtwDates(
            new Date(year, month, 1),
            new Date(year, month + 1, 0, 23, 59, 59, 999),
          );
        }}
        disabledDates={disabledDates}
        year={year}
        disabledDays={[0, 6]}
      />
      <AddEditDetailsDrawer
        openDrawer={addEventFormOpen}
        setDrawerOpen={bool => setShowAddEventForm(bool)}
        onSubmitForm={values => onSubmitForm(values)}
        loading={bookTastingLoading}
        selectedStaffingDay={selectedStaffingDay}
        fromToDates={fromToDates}
      />
      <ConfirmationModal
        {...confirmationProps}
        setOpen={(bool: boolean) => {
          setConfirmationProps({ isOpen: bool });
        }}
      >
        {confirmationProps?.date ? (
          <ReservedSuccess date={confirmationProps.date} />
        ) : (
          <ReservationCancelled />
        )}
      </ConfirmationModal>
      {isRemarksOpen && (
        <RequestsRemarks refetchMainCalendarData={refetchGetBookingsBtwDates} />
      )}
    </Row>
  );
};

const AddEditDetailsDrawer = ({
  openDrawer,
  setDrawerOpen,
  fromToDates,
  selectedStaffingDay,
  onSubmitForm,
  loading,
}: {
  openDrawer: boolean;
  setDrawerOpen: (bool: boolean) => any;
  onSubmitForm: (bool: boolean) => any;
  fromToDates: Date[] | undefined;
  selectedStaffingDay: ISelectedDays | undefined;
  loading: boolean;
}) => {
  const isMobile = useIsMobile();

  return (
    <DrawerCommon
      isVisible={openDrawer}
      setVisibility={setDrawerOpen}
      placement='right'
      styles={{ width: isMobile ? '100%' : '22%' }}
    >
      {fromToDates && openDrawer && (
        <OrderRequestForm
          fromDate={fromToDates[0]}
          loading={loading}
          closeForm={() => {
            setDrawerOpen(false);
          }}
          onFormSubmit={values => {
            onSubmitForm(values);
          }}
        />
      )}
    </DrawerCommon>
  );
};

const CalendarWrapper: React.FC<ICalendarCommon> = ({ ...props }) => {
  const isMobile = useIsMobile();

  return (
    <div
      className='calendar-container'
      style={{ minHeight: !isMobile ? '100%' : '' }}
    >
      {isMobile ? (
        <CalendarMobile {...props} />
      ) : (
        <Card
          bordered={false}
          styles={{
            body: {
              width: '100%',
              ...displayCenter,
              height: '100%',
            },
          }}
          className='calendar-card'
        >
          <CalendarCommon {...props} />
        </Card>
      )}
    </div>
  );
};
